// ============================================================
// App.js — Root component
// Responsibilities:
//   - Theme toggle (dark/light, persisted to localStorage)
//   - Hero section (typewriter name + role cycling)
//   - Layout: Header → SideNavbar → Hero → Works → Certs → About
// To add a section: export from Sections.js, import here, add to <main>
// ============================================================

// ── All imports first — ESLint import/first requires no code between these ──

// ============================================================
// App.js — Optimized
// ============================================================

import React, { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet';
import {
  CssBaseline, ThemeProvider, IconButton, Tooltip, Zoom,
  Typography, makeStyles, useTheme, Link as MuiLink,
} from '@material-ui/core';
import { Brightness4, Brightness7, GitHub, LinkedIn } from '@material-ui/icons';
import { getAppTheme } from './Shared';
import Personal from './data/personal.json';
import { Header, SideNavbar } from './components/Navigation';
import NeonCursor from './components/background/NeonCursor';
import VantaFog from './components/background/VantaFog';

// ── Lazy sections ──
const Works        = lazy(() => import('./components/Sections').then(m => ({ default: m.Works })));
const Certificates = lazy(() => import('./components/Sections').then(m => ({ default: m.Certificates })));
const About        = lazy(() => import('./components/Sections').then(m => ({ default: m.About })));

// Stable public URLs (no fingerprinting) → preload in index.html now works
const getHeroSrc = (isDark) => isDark ? '/images/Dev-d2.webp' : '/images/Dev-l2.webp';

// Social icons
const ICON_MAP = {
  github:   <GitHub />,
  linkedin: <LinkedIn />,
};

const ROLES = ['Full Stack Developer', 'Web Engineer', 'Product Builder'];

// ============================================================
// STYLES
// ============================================================
const useStyles = makeStyles((theme) => ({
  heroSection: {
    width:          '100%',
    minHeight:      '100vh',
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    justifyContent: 'center',
    padding:        theme.spacing(6, 3),
    gap:            theme.spacing(10),
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      gap:           theme.spacing(10),
      padding:       theme.spacing(0, 8),
    },
  },

  heroImageWrap: {
    position:  'relative',
    flexShrink: 0,
    '&::before': {
      content:      '""',
      position:     'absolute',
      inset:        -20,
      background:   'radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 70%)',
      borderRadius: 40,
      zIndex:       -1,
      opacity:      0,
      transition:   'opacity 0.4s ease',
    },
    '&:hover::before': { opacity: 1 },
  },

  heroImage: {
    width:        '100%',
    maxWidth:     'clamp(240px, 34vw, 360px)',
    borderRadius: 24,
    // CLS FIX: aspect-ratio reserves space before image loads without
    // locking a fixed pixel size — clamp() still controls width responsively.
    // Adjust ratio if your webp isn't square (e.g. '3 / 4').

    transition:   'all 0.5s cubic-bezier(0.23, 1, 0.32, 1)',
    '&:hover': {
      transform: 'scale(1.03)',
      filter: theme.palette.type === 'dark'
        ? 'drop-shadow(0 0 35px rgba(247, 247, 247, 0.7))'
        : 'drop-shadow(0 25px 50px rgba(40, 30, 235, 0.78))',
    },
  },

  heroText: {
    display:       'flex',
    flexDirection: 'column',
    alignItems:    'flex-start',
    [theme.breakpoints.down('sm')]: { alignItems: 'center', textAlign: 'center' },
  },

  // CLS FIX: height locked so scramble animation never shifts layout.
  // height:1.5em matches lineHeight:1.5 — text scrambles inside the box.
  nameText: {
    fontFamily:           '"Space Mono", monospace',
    fontSize:             'clamp(2.4rem, 6vw, 4rem)',
    fontWeight:           700,
    lineHeight:           1.5,
    height:               '1.5em',
    overflow:             'wrap',
    display:              'block',
    marginBottom:         theme.spacing(1),
    background: theme.palette.type === 'dark'
      ? 'linear-gradient(90deg, #f8fafc 30%, #6366f1 70%)'
      : 'linear-gradient(90deg, #0f172a 30%, #4f46e5 70%)',
    WebkitBackgroundClip: 'text',

    WebkitTextFillColor:  'transparent',
  },

  // CLS FIX: same lock for tagline scramble
  taglineText: {
    fontFamily:    '"Space Mono", monospace',
    fontSize:      'clamp(0.9rem, 1.8vw, 1.1rem)',
    color:         theme.palette.type === 'dark' ? '#cbd5e1' : '#475569',
    letterSpacing: '0.2em',
    height:        '1.5em',
    overflow:      'wrap',
    display:       'block',
    marginBottom:  theme.spacing(6),
  },

  // CLS FIX: height locked so typewriter characters don't shift layout.
  // Was minHeight:'2em' which still allowed reflow as text grew.
  // height:'1.6em' + overflow:hidden = box is fixed, text types inside it.
  roleText: {
    fontFamily: '"Space Mono", monospace',
    fontSize:   'clamp(1rem, 2.5vw, 1.6rem)',
    color:      theme.palette.type === 'dark' ? '#03c430b6' : '#2b8905be',
    height:     '1.6em',
    overflow:   'hidden',
    display:    'block',
    '&::after': {
      content:   '"_"',
      animation: '$blink 1.1s step-end infinite',
    },
  },

  '@keyframes blink': { '50%': { opacity: 0 } },

  socialTop: {
    position:       'fixed',
    top:            theme.spacing(3),
    right:          theme.spacing(3),
    zIndex:         1100,
    display:        'flex',
    gap:            theme.spacing(0.5),
    background: theme.palette.type === 'dark'
      ? 'rgba(17, 17, 17, 0.85)'
      : 'rgba(255,255,255,0.85)',
    backdropFilter: 'blur(12px)',
    borderRadius:   50,
    padding:        theme.spacing(0.75, 1.5),
    border:         `1px solid ${theme.palette.divider}`,
  },
}));


// ============================================================
// HERO
// ============================================================
const Hero = () => {
  const classes = useStyles();
  const theme = useTheme();

  // Scramble + typewriter effects (unchanged)
  const [displayName, setDisplayName] = useState('');
  const [displayTagline, setDisplayTagline] = useState('');
  const [displayRole, setDisplayRole] = useState('');
  const [roleIndex, setRoleIndex] = useState(0);

  // ... (all useEffect for scramble and typewriter are exactly the same as before) ...
    useEffect(() => {
    let active    = true;
    const chars   = '-./*!?#%&@$€()[]{}<>~';
    let iteration = 0;

    const interval = setInterval(() => {
      if (!active) return;
      setDisplayName(
        Personal.name.split('').map((char, i) =>
          i < iteration ? char : chars[Math.floor(Math.random() * chars.length)]
        ).join('')
      );
      setDisplayTagline(
        Personal.tagline.split('').map((char, i) =>
          i < iteration * 4 ? char : chars[Math.floor(Math.random() * chars.length)]
        ).join('')
      );
      iteration += 0.4;
      if (iteration >= Personal.name.length) {
        clearInterval(interval);
        if (active) {
          setDisplayName(Personal.name);
          setDisplayTagline(Personal.tagline);
        }
      }
    }, 35);

    return () => { active = false; clearInterval(interval); };
  }, []);
  useEffect(() => {
    let active    = true;
    let charIndex = 0;
    let timeoutId;
    const currentRole = ROLES[roleIndex];

    const interval = setInterval(() => {
      if (!active) return;
      setDisplayRole(currentRole.substring(0, charIndex + 1));
      charIndex++;
      if (charIndex === currentRole.length) {
        clearInterval(interval);
        timeoutId = setTimeout(() => {
          if (active) setRoleIndex(prev => (prev + 1) % ROLES.length);
        }, 2400);
      }
    }, 70);

    return () => { active = false; clearInterval(interval); clearTimeout(timeoutId); };
  }, [roleIndex]);

  const heroSrc = getHeroSrc(theme.palette.type === 'dark');

  return (
    <section id="home" className={classes.heroSection}>
      {/* Social icons (unchanged) */}
      <div className={classes.socialTop}>
        {Personal.socials.map((s) => {
          const icon = ICON_MAP[s.network.toLowerCase()];
          if (!icon) return null;
          return (
            <Tooltip key={s.network} title={s.network} TransitionComponent={Zoom} arrow>
              <MuiLink href={s.url} target="_blank" rel="noopener noreferrer">
                <IconButton size="medium">{icon}</IconButton>
              </MuiLink>
            </Tooltip>
          );
        })}
      </div>

      {/* Hero image - LCP optimized */}
      <div className={classes.heroImageWrap}>
        <img
          src={heroSrc}
          alt={Personal.name}
          className={classes.heroImage}
          fetchpriority="high"
          loading="eager"
        />
      </div>

      {/* Text block (unchanged) */}
      <div className={classes.heroText}>
        <Typography className={classes.nameText}>
          {displayName || '\u00A0'}
        </Typography>
        <Typography className={classes.taglineText}>
          {displayTagline || '\u00A0'}
        </Typography>
        <Typography className={classes.roleText}>
          {displayRole || '\u00A0'}
        </Typography>
      </div>
    </section>
  );
};

// ============================================================
// APP
// ============================================================
const App = () => {
  const [isDark, setIsDark] = useState(() => localStorage.getItem('theme') === 'dark');
  const theme = useMemo(() => getAppTheme(isDark ? 'dark' : 'light'), [isDark]);

  const toggleTheme = () => {
    setIsDark(prev => {
      const next = !prev;
      localStorage.setItem('theme', next ? 'dark' : 'light');
      return next;
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <VantaFog active={true} />
      <NeonCursor />

      <Helmet>
        <title>{Personal.name} | Developer</title>
        <meta name="description" content={Personal.tagline} />
      </Helmet>

      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Header />
        <SideNavbar />

        <Tooltip title={isDark ? 'Light Mode' : 'Dark Mode'} arrow>
          <IconButton
            onClick={toggleTheme}
            style={{
              position: 'fixed',
              bottom: 50,
              right: 20,
              zIndex: 1200,
              background: theme.palette.background.paper,
              backdropFilter: 'blur(12px)',
            }}
          >
            {isDark ? <Brightness7 /> : <Brightness4 />}
          </IconButton>
        </Tooltip>

        <Hero />

        <main>
          <Suspense fallback={null}>
            <Works />
            <Certificates />
            <About />
          </Suspense>
        </main>
      </div>
    </ThemeProvider>
  );
};

export default App;